// 3rd Party
import Analytics from '@opencare/analytics';
import _ from 'lodash';
import moment from 'moment';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { P } from 'common/styles/oc-styled-components';

// Other

import { colors } from 'common/styles/style-constants';
import {
  getInsuranceList,
  getInsuranceListForCanada,
  updateCheckoutFormData,
  updateInsuranceProviderAndPlan,
  updateMemberId,
  updatePatientDetails,
} from 'connect/actions';
import {
  selectDataCheckoutFormData,
  selectDataClinic,
  selectDataClinicCountry,
  selectDataClinicLocality,
  selectDataClinicStateShort,
  selectDataInsurance,
  selectDataLoadingMatch,
  selectDataLoadingUser,
  selectDataMatchId,
  selectDataPatient,
  selectDataRequestedAppointment,
} from 'connect/selectors';
import { Form } from 'opencare-crown';

import { useAnalyticsService } from 'services/AnalyticsService';
import { googlePlaceActionLink } from 'utils/google';
import { getPatientSexAtBirth } from 'utils/patient';

import { COUNTRY } from '../../../../../common/constants';
import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../../providers/WindowDimensionsProvider';
import Error from '../../../../common/components/Error/Error';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import { StepLoading } from '../../../checkout-styled-components';
import {
  makeSelectRequestData,
  selectDataCheckoutFormErrorStatus,
} from '../../../connect/selectors';
import {
  checkMemberIdFormat,
  formatDateOfBirth,
  makeInsuranceState,
} from '../../../helpers/insurance-helpers';
import {
  CheckoutInsuranceForm,
  ContextType,
} from '../../common/CheckoutInsuranceForm/CheckoutInsuranceForm';
import InsuranceSwitchRowForm from '../../common/InsuranceSwitch/InsuranceSwitch';

import { InsuranceStepSubmitButton } from './InsuranceStepSubmitButton';
import PatientInformation from './PatientInformation';
import { Fieldset } from './styled-components';

const analytics = new Analytics();

const INSURANCE_STEPS = {
  PATIENT_INFORMATION: 'patient-information',
  INSURANCE: 'insurance',
};

// Component
const InsuranceStep = ({ handleNextStep, state }) => {
  const funnelStorage = useFunnelStorage();
  const dispatch = useDispatch();
  const [insuranceForm] = Form.useForm();

  const analyticsService = useAnalyticsService();

  const clinic = useSelector(selectDataClinic);
  const country = useSelector(selectDataClinicCountry);
  const city = useSelector(selectDataClinicLocality);
  const insurance = useSelector(selectDataInsurance);
  const patient = useSelector(selectDataPatient);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );
  const checkoutFormData = useSelector(selectDataCheckoutFormData);
  const loadingUser = useSelector(selectDataLoadingUser);
  const requestData = useSelector(makeSelectRequestData);
  const region = useSelector(selectDataClinicStateShort);

  const matchId = useSelector(selectDataMatchId);
  const loadingMatch = useSelector(selectDataLoadingMatch);
  const directLink = !matchId && !loadingMatch;

  const { layout } = useWindowDimensions();
  const splitInsuranceSteps =
    (insurance.provider && layout === DIMENSIONS.MOBILE) || directLink;

  // https://app.asana.com/0/1167683449390038/1204602000857729
  const insuranceCardEnabled = false;

  const [insuranceMemberInformation, setInsuranceMemberInformation] = useState({
    isPrimaryMember:
      country === COUNTRY.unitedStates
      && typeof insurance.isPrimaryHolder !== 'undefined'
        ? insurance.isPrimaryHolder
        : true,
    memberfirstName: state?.memberfirstName || '',
    memberlastName: state?.memberlastName || '',
    memberbirthYear: state?.dateOfBirth?.[0] || '',
    memberbirthMonth: state?.dateOfBirth?.[1] || '',
    memberbirthDay: state?.dateOfBirth?.[2] || '',
    memberId: insurance.memberId || '',
    groupNumber: insurance.groupNumber || '',
    employer: insurance.employer || '',
  });
  const updateInsuranceMemberInformation = (fieldName, value) => {
    const mapping = {
      firstName: 'memberfirstName',
      lastName: 'memberlastName',
      day: 'memberbirthDay',
      month: 'memberbirthMonth',
      year: 'memberbirthYear',
    };

    const mappedFieldName = mapping[fieldName] || fieldName;
    const newInsuranceMemberInformation = {
      ...insuranceMemberInformation,
      [mappedFieldName]: value,
    };

    // Reset fields if isPrimaryMember is unchecked
    if (mappedFieldName === 'isPrimaryMember' && !value) {
      newInsuranceMemberInformation.memberfirstName = '';
      newInsuranceMemberInformation.memberlastName = '';
      newInsuranceMemberInformation.memberbirthMonth = '';
      newInsuranceMemberInformation.memberbirthDay = '';
      newInsuranceMemberInformation.memberbirthYear = '';
    }

    setInsuranceMemberInformation(newInsuranceMemberInformation);
    insuranceForm.setFieldsValue(newInsuranceMemberInformation);
  };
  const { isPrimaryMember } = insuranceMemberInformation;

  const [insuranceProvider, setInsuranceProvider] = useState({
    name: insurance.provider,
    id: insurance.providerId,
  });

  const [insurancePlan, setInsurancePlan] = useState({
    name: insurance.plan,
    id: insurance.planId,
  });
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(
    true,
  );
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [stepComplete, setStepComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(
    INSURANCE_STEPS.PATIENT_INFORMATION,
  );
  const [hasInsuranceDirectLink, setHasInsuranceDirectLink] = useState(null);

  const [firstName, setFirstName] = useState(
    funnelStorage.prefillData.patientFirstName || patient?.firstName || '',
  );
  const [lastName, setLastName] = useState(
    funnelStorage.prefillData.patientLastName || patient?.lastName || '',
  );

  const showReturningPatientToggle = Boolean(
    googlePlaceActionLink.getConversionData().rwgToken,
  );
  const [isReturningPatient, setIsReturningPatient] = useState(
    checkoutFormData.isReturningPatient || false,
  );

  const isMemberIdOptional =
    insuranceFormContext === ContextType.PARTNER
    || clinic.metadata?.isMemberIdOptional === true;

  const onNameChange = (field, value) => {
    const updateFunc = {
      firstName: setFirstName,
      lastName: setLastName,
    };

    updateFunc[field]?.(value);
  };

  const [birthdate, setBirthdate] = useState({
    day: state?.dateOfBirth?.[2] || null,
    month: state?.dateOfBirth?.[1] || null,
    year: state?.dateOfBirth?.[0] || null,
  });
  const onBirthdateChange = (field, value) => {
    const fieldName = {
      day: 'birthDay',
      month: 'birthMonth',
      year: 'birthYear',
    }[field];

    insuranceForm.setFieldsValue({
      [fieldName]: value,
    });

    setBirthdate({
      ...birthdate,
      [field]: value,
    });
  };

  // On initialization check what required fields are left
  // This will enable the continue button if they're already filled
  useEffect(() => {
    checkRequiredFields();
  }, [insuranceForm.getFieldsValue()]);

  // All necessary data is loaded
  useEffect(() => {
    // Prefill form based on patient data
    if (patient) {
      setFirstName(patient.firstName);
      setLastName(patient.lastName);
      if (patient.dateOfBirth) {
        setBirthdate({
          day: moment(patient.dateOfBirth).format('DD'),
          month: moment(patient.dateOfBirth).format('MM'),
          year: moment(patient.dateOfBirth).format('YYYY'),
        });
      }
      setHasInsuranceDirectLink(!!insurance.provider);
      setInsuranceProvider({
        name: insurance.provider,
        id: insurance.providerId,
      });
      setInsurancePlan({
        name: insurance.plan,
        id: insurance.planId,
      });
      setIsLoading(false);
    }
  }, [patient]);

  // Initial page view event
  useEffect(() => {
    if (insurance && patient && requestedAppointment.providerId) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM,
        pageName: `form-booking-patient-information${
          insurance.provider ? '-insurance' : ''
        }`,
        name: 'form-booking',
        section: insurance.provider
          ? 'patient-information-insurance'
          : 'patient-details',
        content: {
          'email-verified': patient.emailVerifiedAt ? 'yes' : 'no',
          'phone-verified': patient.phoneVerifiedAt ? 'yes' : 'no',
          'patient-country': country,
        },
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        patientId: patient.id,
        ...analyticsService.analyticsData,
      });
    }
  }, [insurance, patient, requestedAppointment]);

  // To move to the next step one of the following must be true:
  //   1. must be a new user who filled out the form
  //   2. or a user who is already signed in who clicks continue
  useEffect(() => {
    const canMoveToNextStep =
      stepComplete && !loadingUser && !checkoutFormErrorStatus;

    if (canMoveToNextStep) {
      analytics.trackForm({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section: 'auto-verify-email-phone',
        content: {
          'email-risk-score': String(
            _.get(patient.metadata, 'socureData.emailRisk.score'),
          ),
          'name-email-correlation-score': String(
            _.get(patient.metadata, 'socureData.nameEmailCorrelation.score'),
          ),
          'email-risk-threshold': String(
            _.get(
              patient.metadata,
              'emailAutoVerificationThresholds.socureRiskMax',
            ),
          ),
          'name-email-correlation-threshold': String(
            _.get(
              patient.metadata,
              'emailAutoVerificationThresholds.socureNameCorrelationMin',
            ),
          ),
          'email-auto-verified':
            patient.emailVerifiedAt
            && _.get(patient.metadata, 'emailAutoVerified')
              ? 'yes'
              : 'no',
          'phone-risk-score': String(
            _.get(patient.metadata, 'socureData.phoneRisk.score'),
          ),
          'name-phone-correlation-score': String(
            _.get(patient.metadata, 'socureData.namePhoneCorrelation.score'),
          ),
          'phone-risk-threshold': String(
            _.get(
              patient.metadata,
              'phoneAutoVerificationThresholds.socureRiskMax',
            ),
          ),
          'name-phone-correlation-threshold': String(
            _.get(
              patient.metadata,
              'phoneAutoVerificationThresholds.socureNameCorrelationMin',
            ),
          ),
          'phone-auto-verified':
            patient.phoneVerifiedAt
            && _.get(patient.metadata, 'phoneAutoVerified')
              ? 'yes'
              : 'no',
          'patient-country': _.get(patient.address, 'country'),
        },
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        patientId: patient.id,
      });

      handleNextStep();
    }
  }, [patient, loadingUser, stepComplete]);

  // Reset the insurance form initial values when prefill data
  // for the first and last name values exist.
  useEffect(() => {
    if (
      funnelStorage.prefillData.patientFirstName
      || funnelStorage.prefillData.patientLastName
    ) {
      insuranceForm.resetFields();
    }
  }, [funnelStorage.prefillData]);

  useEffect(() => {
    if (country === COUNTRY.canada) {
      dispatch(getInsuranceListForCanada());
    }
  }, [country]);

  useEffect(() => {
    if (region) {
      dispatch(getInsuranceList(region));
    }
  }, [region]);

  const checkMemberId = (_rule, value, callback) => {
    if (!value && isMemberIdOptional) {
      return callback();
    }

    if (!value) {
      return callback('Please enter a member ID.');
    }

    const checkResult = checkMemberIdFormat(value);

    if (!checkResult.valid) {
      return callback(checkResult.description);
    }

    return callback();
  };

  const fireSubmitAnalytics = () => {
    let content = {
      first: insuranceForm.getFieldValue('firstName'),
      last: insuranceForm.getFieldValue('lastName'),
      birth: moment(
        `${insuranceForm.getFieldValue('birthYear')}-${moment()
          .month(
            typeof insuranceForm.getFieldValue('birthMonth') === 'number'
              ? insuranceForm.getFieldValue('birthMonth') - 1
              : insuranceForm.getFieldValue('birthMonth'),
          )
          .format('MM')}-${insuranceForm.getFieldValue('birthDay')}`,
      ).format('YYYY-MM-DD'),
      gender: getPatientSexAtBirth(null, { requestData }),
      'patient-country': country,
      'is-returning-patient': isReturningPatient ? 'yes' : 'no',
    };

    if (insurance.provider && hasInsuranceDirectLink !== false) {
      const insuranceContent = {
        holder: isPrimaryMember ? 'yes' : 'no',
        'insurance-carrier': insuranceProvider.name,
        'member-id': insuranceForm.getFieldValue('memberId').toString(),
        employer: insuranceForm.getFieldValue('employer') || null,
      };
      if (country === COUNTRY.unitedStates) {
        insuranceContent['insurance-plan'] = insurancePlan.name;
        insuranceContent['group-id'] =
          insuranceForm.getFieldValue('groupId') || null;
      }
      content = _.merge(content, insuranceContent);
    }

    if (!isPrimaryMember) {
      const nonMemberContent = {
        'member-first': insuranceForm.getFieldValue('memberfirstName'),
        'member-last': insuranceForm.getFieldValue('memberlastName'),
        'member-birth': moment(
          `${insuranceForm.getFieldValue('memberbirthYear')}-${moment()
            .month(insuranceForm.getFieldValue('memberbirthMonth') - 1)
            .format('MM')}-${insuranceForm.getFieldValue('memberbirthDay')}`,
        ).format('YYYY-MM-DD'),
      };
      content = _.merge(content, nonMemberContent);
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: `form-booking-patient-information${
        insurance.provider ? '-insurance' : ''
      }`,
      name: 'form-booking',
      section: insurance.provider
        ? 'patient-information-insurance'
        : 'patient-details',
      content,
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
    });
  };

  const checkRequiredFields = () => {
    const formValues = insuranceForm.getFieldsValue();

    // Disabling the continue button until all required inputs have been filled
    const requiredInputValues = [
      insuranceForm.getFieldValue('firstName'),
      insuranceForm.getFieldValue('lastName'),
      insuranceForm.getFieldValue('birthMonth'),
      insuranceForm.getFieldValue('birthDay'),
      insuranceForm.getFieldValue('birthYear'),
    ];

    let everyFieldIsFilled = requiredInputValues.every(
      (input) => input !== '' && input !== null && input !== undefined,
    );

    // Doing it here so that when future fields are undefined, person can proceed as long as first 5 are
    if (directLink) {
      setIsNextButtonDisabled(
        !everyFieldIsFilled || hasInsuranceDirectLink === null,
      );
    } else {
      setIsNextButtonDisabled(
        !everyFieldIsFilled || !_.get(requestData, 'sexAtBirth'),
      );
    }

    if (insurance.provider || hasInsuranceDirectLink) {
      if (!isMemberIdOptional) {
        requiredInputValues.push(insuranceForm.getFieldValue('memberId'));
      }

      if (country === COUNTRY.unitedStates) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('insuranceCarrierUS'),
          insuranceForm.getFieldValue('insurancePlanUS'),
        );
      }

      if (country === COUNTRY.canada) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('insuranceCarrierCanada'),
        );
      }

      if (!formValues.isPrimaryMember) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('memberfirstName'),
          insuranceForm.getFieldValue('memberlastName'),
          insuranceForm.getFieldValue('memberbirthMonth'),
          insuranceForm.getFieldValue('memberbirthDay'),
          insuranceForm.getFieldValue('memberbirthYear'),
        );
      }
    }

    everyFieldIsFilled = requiredInputValues.every(
      (input) => input !== '' && input !== null && input !== undefined,
    );

    setIsContinueButtonDisabled(
      !everyFieldIsFilled || !_.get(requestData, 'sexAtBirth'),
    );
  };

  const handleFormSubmitDirectLink = () => {
    const patientDetails = {
      firstName: insuranceForm.getFieldValue('firstName'),
      lastName: insuranceForm.getFieldValue('lastName'),
      address: {
        country,
        city,
      },
      gender: getPatientSexAtBirth(null, { requestData }),
      memberFirstName: insuranceForm.getFieldValue('firstName'),
      memberLastName: insuranceForm.getFieldValue('lastName'),
      memberDateOfBirth: formatDateOfBirth(
        insuranceForm.getFieldValue('birthYear'),
        insuranceForm.getFieldValue('birthMonth'),
        insuranceForm.getFieldValue('birthDay'),
      ),
    };

    dispatch(updatePatientDetails(patientDetails, true));
    // reset insurance
    dispatch(updateInsuranceProviderAndPlan({}));
    dispatch(updateMemberId(undefined));

    fireSubmitAnalytics();

    dispatch(updateCheckoutFormData(Object.assign({}, patientDetails)));

    if (checkoutFormErrorStatus === null && !loadingUser) {
      setStepComplete(true);
    }
  };

  const handleFormSubmit = (values) => {
    const patientDetails = {};

    const insuranceMemberMetadata = {
      primaryInsuranceMember: values.isPrimaryMember,
      memberFirstName: values.memberfirstName,
      memberLastName: values.memberlastName,

      memberDateOfBirth: values.isPrimaryMember
        ? undefined
        : formatDateOfBirth(
          values.memberbirthYear,
          values.memberbirthMonth,
          values.memberbirthDay,
        ),

    };
    patientDetails.metadata = patient
      ? Object.assign({}, patient.metadata, insuranceMemberMetadata)
      : {};
    patientDetails.firstName = values.firstName || patient.firstName;
    patientDetails.lastName = values.lastName || patient.lastName;
    patientDetails.address = {
      country,
      city,
    };
    patientDetails.dateOfBirth =
      formatDateOfBirth(values.birthYear, values.birthMonth, values.birthDay)
      || patient.dateOfBirth;
    patientDetails.gender = getPatientSexAtBirth(null, { requestData });

    dispatch(updatePatientDetails(patientDetails, true));

    const newInsurance = makeInsuranceState(insuranceProvider, insurancePlan);
    const toSave = Object.assign(
      {
        isReturningPatient: showReturningPatientToggle
          ? isReturningPatient
          : null,
      },
      values,
      newInsurance,
    );
    dispatch(updateInsuranceProviderAndPlan(toSave));
    dispatch(updateMemberId(values.memberId));

    fireSubmitAnalytics();

    dispatch(updateCheckoutFormData(toSave));

    if (checkoutFormErrorStatus === null && !loadingUser) {
      setStepComplete(true);
    }
  };

  const handleUpdateInsurance = (newProvider, newPlan) => {
    setInsuranceProvider(newProvider);
    if (country === COUNTRY.unitedStates) {
      setInsurancePlan(newPlan);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  const showPatientInformationSection =
    !splitInsuranceSteps || currentStep === INSURANCE_STEPS.PATIENT_INFORMATION;
  const showInsuranceSection =
    !splitInsuranceSteps || currentStep === INSURANCE_STEPS.INSURANCE;
  const insuranceFormContext = splitInsuranceSteps
    ? ContextType.PATIENT_SPLIT_STEPS
    : ContextType.PATIENT;

  const hasInsurance = Boolean(insurance.provider || hasInsuranceDirectLink);
  return (
    <>
      {checkoutFormErrorStatus
        && (
          <Error style={{ marginBottom: '30px' }}>
            <P color={colors.danger} data-testid="error">
              Something went wrong while submitting your details.
            </P>
          </Error>
        )}
      <Form
        form={insuranceForm}
        data-testid="insuranceForm"
        onFinish={handleFormSubmit}
        onChange={checkRequiredFields}
      >
        <>
          <Fieldset
            margin="0 0 20px"

            /*
             * We hide this section using CSS instead of JS because
             * the ant `Form` clears the data when it's hidden
             */
            hide={splitInsuranceSteps && !showPatientInformationSection}
          >
            <PatientInformation
              firstName={firstName}
              lastName={lastName}
              onNameChange={onNameChange}
              birthdate={birthdate}
              onBirthdateChange={onBirthdateChange}
              insurance={insurance}
              insuranceForm={insuranceForm}
              state={state}
              checkRequiredFields={checkRequiredFields}
              showReturningPatientToggle={showReturningPatientToggle}
              isReturningPatient={isReturningPatient}
              onReturningPatientChange={setIsReturningPatient}
            />
            {directLink
              && (
                <InsuranceSwitchRowForm
                  defaultValue={hasInsuranceDirectLink}
                  handleValueChange={setHasInsuranceDirectLink}
                />
              )}
          </Fieldset>

          {showPatientInformationSection && splitInsuranceSteps
            && (
              <InsuranceStepSubmitButton
                text="Next Step"
                isDisabled={isNextButtonDisabled}
                onClick={() =>
                  directLink && !hasInsuranceDirectLink
                    ? handleFormSubmitDirectLink()
                    : setCurrentStep(INSURANCE_STEPS.INSURANCE)}
                testId="insuranceNextButton"
              />
            )}

          {showInsuranceSection
            && (
              <>
                {hasInsurance
                  && (
                    <CheckoutInsuranceForm
                      insuranceProvider={insuranceProvider}
                      insuranceMemberId={insuranceMemberInformation.memberId}
                      insuranceEmployer={insuranceMemberInformation.employer}
                      groupNumber={insuranceMemberInformation.groupNumber}
                      insurancePlan={insurancePlan}
                      insuranceForm={insuranceForm}
                      checkRequiredFields={checkRequiredFields}
                      country={country}
                      handleUpdateInsurance={handleUpdateInsurance}
                      insuranceCardEnabled={insuranceCardEnabled}
                      checkMemberId={checkMemberId}
                      patient={patient}
                      contextType={insuranceFormContext}
                      onMemberInformationChange={updateInsuranceMemberInformation}
                      memberInformation={insuranceMemberInformation}
                      isMemberIdOptional={isMemberIdOptional}
                    />
                  )}
                <InsuranceStepSubmitButton
                  text="Continue"
                  isDisabled={isContinueButtonDisabled}
                  isLoading={stepComplete}
                  testId="insuranceSubmitButton"
                />
              </>
            )}
        </>
      </Form>
    </>
  );
};

InsuranceStep.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
};

export default InsuranceStep;
